import React, { useEffect, useRef, useState } from 'react';

import {
  getDateAndTimeModifiedFormat,
  getDateModifiedFormat,
  getDateModifiedFormat2,
} from '../../utils/commonUtils';
import PropTypes from 'prop-types';

const DateSelector = ({
  onDateChange,
  buttonTitle = 'Set Date',
  dateFormat2 = false,
  dateFormat3 = false,
  displayButton = true,
}) => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0'); // month is 0-indexed
  const dd = String(today.getDate()).padStart(2, '0');

  const defaultFrom = `${yyyy}-${mm}-01`;
  const defaultTo = `${yyyy}-${mm}-${dd}`;

  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);

  const isFirstRender = useRef(true);

  const formatAndEmit = (from, to) => {
    if (!from || !to || !onDateChange) return;

    let formattedFrom, formattedTo;

    if (dateFormat3) {
      formattedFrom = getDateModifiedFormat(new Date(from));
      formattedTo = getDateModifiedFormat(new Date(to));
    } else if (dateFormat2) {
      formattedFrom = getDateModifiedFormat2(new Date(from));
      formattedTo = getDateModifiedFormat2(new Date(to));
    } else {
      formattedFrom = getDateAndTimeModifiedFormat(new Date(from));
      formattedTo = getDateAndTimeModifiedFormat(new Date(to));
    }

    onDateChange(formattedFrom, formattedTo);
  };

  const handleSetDate = () => {
    if (fromDate && toDate && onDateChange) {
      formatAndEmit(fromDate, toDate);
    } else {
      alert('All the fields are required');
    }
  };

  // Auto-emit on change when displayButton is false, skipping the initial mount
  useEffect(() => {
    if (displayButton) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    formatAndEmit(fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, displayButton]);

  return (
    <div className="w-full flex items-end justify-start space-x-3 bg-[#151C2E]">
      {/* From Date */}
      <div className="relative">
        <label className="absolute -top-2 left-3 bg-[#151C2E] px-1 text-xs z-50 font-semibold text-[#ffff]">
          From
        </label>
        <div className="relative">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border bg-[#151C2E] text-white rounded-full px-3 py-1 w-[140px]
               pl-9 focus:outline-none focus:ring-1 focus:ring-white text-sm appearance-none"
          />

          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white pointer-events-none"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            viewBox="0 0 24 24"
          >
            <path d="M8 7V3m8 4V3M3 11h18M5 5h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
          </svg>

          <style>
            {`
      input[type="date"]::-webkit-calendar-picker-indicator {
        opacity: 0;
        position: absolute;
        right: 0;
        width: 100%;
        height: 100%;
        cursor: pointer;
      }
    `}
          </style>
        </div>
      </div>

      {/* To Date */}
      <div className="relative">
        <label className="absolute -top-2 z-50 left-3 bg-[#151C2E] px-1 text-xs font-semibold text-white">
          To
        </label>
        <div className="relative">
          <input
            type="date"
            value={toDate}
            min={fromDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border bg-[#151C2E] text-white rounded-full pl-10 pr-3 py-1 w-[140px]
               focus:outline-none focus:ring-1 focus:ring-white text-sm appearance-none"
            style={{ borderColor: '#ffff' }}
          />

          {/* Custom Calendar Icon */}
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white pointer-events-none"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            viewBox="0 0 24 24"
          >
            <path d="M8 7V3m8 4V3M3 11h18M5 5h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
          </svg>
        </div>
      </div>

      {/* Set Date Button */}
      {displayButton && (
        <button
          onClick={handleSetDate}
          className="bg-[#FF9900] text-[#ffff] px-3 py-2 rounded-full hover:bg-[#cc7a00] text-sm"
        >
          {buttonTitle}
        </button>
      )}
    </div>
  );
};

export default DateSelector;

DateSelector.propTypes = {
  onDateChange: PropTypes.func.isRequired,
  buttonTitle: PropTypes.string,
  dateFormat2: PropTypes.bool,
  dateFormat3: PropTypes.bool,
  displayButton: PropTypes.bool,
};
